import axios from 'axios';

import createUrlWithParams from 'helpers/createUrlWithParams';
import { Id } from 'types/Id';

import METHOD from '../_methods';
import { AssetTaskStatus } from '../_types/AssetTaskStatus';
import { BasePaginatedQueryParams } from '../_types/BasePaginatedQueryParams';
import { AssetRiskImportance, RiskPlace, RiskScaleValue } from '../assetRisks/assetRisks.types';

import {
  parseSingleAssetRiskTasksForFE,
  parseAllAssetRiskTasksForFE,
  parseCreateAssetRiskFormInput,
  parseUpdateAssetRiskFormInput,
  parseAssetRiskTasksStatsForFE,
} from './assetRiskTasks.parsers';
import AssetRiskTasksApi, { AssetRiskEndpoints } from './assetRiskTasks.types';

const endpointsDict: Record<AssetRiskEndpoints, string> = {
  general: 'api/v1/asset_risk_tasks/',
  user: 'api/v1/active_user/asset_risk_tasks/',
  organization: 'api/v1/organization/asset_risk_tasks/',
  managementReport: 'api/v1/management_report_risk_tasks/',
};

export type AssetRiskTasksListBaseParams = {
  is_overdue: boolean;
  management_report: Id;
  date_from: string;
  date_to: string;
  management_status: string;
  risk: Id;
  risk__affect_individuals: boolean;
  risk__place: RiskPlace;
  risk__risk_analysis: Id;
  risk__risk_analysis__asset: Id;
  risk__risk_analysis__asset__info__assets_importance: AssetRiskImportance;
  risk__risk_analysis__asset__name: string;
  risk__risk_analysis__asset__owner: Id;
  risk__risk_quadrant: RiskScaleValue;
  status: AssetTaskStatus;
};

export const legacyAssetRiskTasksApi = {
  getAllAssetRiskTasks:
    (endpoint: AssetRiskEndpoints = 'general', baseParams?: Partial<AssetRiskTasksListBaseParams>) =>
    (params: BasePaginatedQueryParams) => ({
      url: createUrlWithParams(endpointsDict[endpoint], { ...(baseParams || {}), ...params }),
      method: METHOD.GET,
    }),
};

const assetRiskTasksApi: AssetRiskTasksApi = {
  getAllAssetRiskTasks: async (riskAnalysisId, endpoint = 'general') => {
    const { data } = await axios.get(createUrlWithParams(endpointsDict[endpoint], { risk__risk_analysis: riskAnalysisId }));
    return parseAllAssetRiskTasksForFE(data);
  },
  getSingleAssetRiskTasks: async (id, endpoint = 'general') => {
    const { data } = await axios.get(`${endpointsDict[endpoint]}${id}/`);
    return parseSingleAssetRiskTasksForFE(data);
  },
  getAssetRiskTasksStats: async filters => {
    const { data } = await axios.get(createUrlWithParams(`${endpointsDict.organization}stats/`, filters));
    return parseAssetRiskTasksStatsForFE(data);
  },
  getManagementReportRiskTasksStats: async filters => {
    const { data } = await axios.get(createUrlWithParams(`${endpointsDict.managementReport}stats/`, filters));
    return parseAssetRiskTasksStatsForFE(data);
  },
  removeAssetRiskTask: id => axios.delete(`${endpointsDict.general}${id}/`),
  createAssetRiskTask: data => axios.post(endpointsDict.general, parseCreateAssetRiskFormInput(data)),
  updateAssetRiskTask: ({ id, data, endpoint = 'general' }) => {
    const dataToUpdate = parseUpdateAssetRiskFormInput(data);
    if (!dataToUpdate) return Promise.resolve();
    return axios.patch(`${endpointsDict[endpoint]}${id}/`, dataToUpdate);
  },
};

export default assetRiskTasksApi;
